import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, LogIn, LogOut, Loader2, AlertTriangle } from "lucide-react";
import { useTimeClock, type JobCandidate } from "@/hooks/useTimeClock";
import { JobConfirmDialog } from "./JobConfirmDialog";
import { haversineMeters, ON_SITE_RADIUS_M, formatDistance, formatDuration } from "@/lib/geoUtils";
import { AddressFromCoords } from "./AddressFromCoords";
import { toast } from "sonner";

interface Props {
  source?: "mobile" | "dispatch";
}

export function TimeClockPunchCard({ source = "mobile" }: Props) {
  const { activeEntry, loading, busy, findSuggestedJob, clockIn, clockOut } = useTimeClock();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState<JobCandidate[]>([]);
  const [best, setBest] = useState<JobCandidate | null>(null);
  const [pendingPos, setPendingPos] = useState<GeolocationPosition | null>(null);
  const [locating, setLocating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Live duration ticker
  useEffect(() => {
    if (!activeEntry?.id) return;
    const i = window.setInterval(() => setTick((t) => t + 1), 30000);
    return () => window.clearInterval(i);
  }, [activeEntry?.id]);

  const handlePunchInClick = () => {
    // CRITICAL: call geolocation synchronously inside the click handler so the browser
    // keeps the user-gesture context. Do not await anything before this call.
    if (!("geolocation" in navigator)) {
      const message = "This browser doesn't support GPS. Use a phone or a browser with location enabled.";
      setStatusMessage(message);
      toast.error(message);
      return;
    }
    setLocating(true);
    setStatusMessage(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          setPendingPos(pos);
          const { best, candidates, hasScheduledJobs } = await findSuggestedJob(
            pos.coords.latitude,
            pos.coords.longitude,
            { includeAllScheduledToday: source === "dispatch" }
          );

          if (!hasScheduledJobs || !best) {
            const message = "No job is assigned to you for today. Dispatch must assign your job first.";
            setStatusMessage(message);
            toast.error(message);
            setBest(null);
            setCandidates([]);
            setDialogOpen(true);
            return;
          }

          setBest(best);
          setCandidates(candidates);
          setDialogOpen(true);
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "GPS permission denied. Location must be enabled to clock in."
            : err.code === err.POSITION_UNAVAILABLE
            ? "GPS unavailable. Try again outside or check device location settings."
            : err.code === err.TIMEOUT
            ? "GPS timed out. Try again while standing at the job site."
            : "Couldn't read GPS. Try again with location enabled.";
        setStatusMessage(msg);
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleConfirm = async (jobId: string, reason?: string) => {
    const job = candidates.find((c) => c.id === jobId);
    const jobHasCoordinates = job?.latitude != null && job?.longitude != null;
    const distance =
      pendingPos && job && jobHasCoordinates
        ? haversineMeters(
            pendingPos.coords.latitude,
            pendingPos.coords.longitude,
            Number(job.latitude),
            Number(job.longitude)
          )
        : null;
    const offSite = jobHasCoordinates ? (distance == null ? false : distance > ON_SITE_RADIUS_M) : false;
    try {
      await clockIn({
        jobId,
        lat: pendingPos?.coords.latitude ?? 0,
        lng: pendingPos?.coords.longitude ?? 0,
        accuracy: pendingPos?.coords.accuracy,
        distanceM: distance,
        offSite,
        offSiteReason: reason,
        source,
      });
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Failed to clock in");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (activeEntry) {
    const minutes = Math.max(
      0,
      Math.floor((Date.now() - new Date(activeEntry.clock_in_at).getTime()) / 60000)
    );
    void tick;
    return (
      <Card className="border-success/40 bg-success/5">
        <CardContent className="space-y-3 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-success">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                On the clock
              </div>
              <div className="mt-1 flex items-center gap-2 text-2xl font-bold tabular-nums">
                <Clock className="h-5 w-5 text-muted-foreground" />
                {formatDuration(minutes)}
              </div>
              {activeEntry.job?.property_address && (
                <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{activeEntry.job.property_address}</span>
                </div>
              )}
              {activeEntry.clock_in_lat != null && activeEntry.clock_in_lng != null && (
                <div className="mt-1 text-xs text-muted-foreground">
                  <AddressFromCoords lat={activeEntry.clock_in_lat} lng={activeEntry.clock_in_lng} />
                  {activeEntry.clock_in_accuracy != null && (
                    <span className="ml-1 tabular-nums">· ±{Math.round(activeEntry.clock_in_accuracy)}m</span>
                  )}
                </div>
              )}
              {activeEntry.off_site_flag && (
                <Badge variant="outline" className="mt-2 border-warning text-warning">
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  Off-site
                  {activeEntry.distance_from_job_m != null &&
                    ` · ${formatDistance(activeEntry.distance_from_job_m)} away`}
                </Badge>
              )}
            </div>
            <Button
              size="lg"
              variant="destructive"
              onClick={clockOut}
              disabled={busy}
              className="shrink-0"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              <span className="ml-2">Clock out</span>
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground">
            GPS pinged every 5 min while you're on the clock.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="flex items-center justify-between gap-3 p-4">
          <div>
            <div className="text-sm font-medium">Not clocked in</div>
            <div className="text-xs text-muted-foreground">
              Tap to punch in. We'll auto-detect your job from GPS.
            </div>
            {statusMessage && (
              <div className="mt-2 text-xs text-warning">
                {statusMessage}
              </div>
            )}
            {pendingPos && (
              <div className="mt-2 text-xs text-muted-foreground">
                <span className="text-success font-medium">GPS locked:</span>{" "}
                <AddressFromCoords lat={pendingPos.coords.latitude} lng={pendingPos.coords.longitude} showIcon={false} />
                <span className="ml-1 tabular-nums">· ±{Math.round(pendingPos.coords.accuracy)}m</span>
              </div>
            )}
          </div>
          <Button size="lg" onClick={handlePunchInClick} disabled={locating || busy}>
            {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            <span className="ml-2">Clock in</span>
          </Button>
        </CardContent>
      </Card>
      <JobConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        candidates={candidates}
        best={best}
        onConfirm={handleConfirm}
        onRejectSuggested={() => {
          toast.error("Dispatch needs to correct today’s assignment before you can clock in.");
          setDialogOpen(false);
        }}
        busy={busy}
      />
    </>
  );
}
